import { db } from '@/config/firebase';

class FirebaseAdsService {
  constructor() {
    this.collectionName = 'ads';
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Fetch ads data from Firestore
   * @returns {Promise<Array>} Array of ads documents
   */
  async fetchAds() {
    if (!db) {
      throw new Error('Firebase not initialized. Check Firebase configuration.');
    }

    try {
      const { collection, getDocs, query, where, orderBy } = window.Firebase;
      
      // Create simple query to fetch all ads (filter in code for now)
      const adsCollection = collection(db, this.collectionName);
      const adsQuery = query(
        adsCollection,
        where('isActive', '==', true),
        orderBy('priority', 'asc')
      );

      const querySnapshot = await getDocs(adsQuery);
      
      const ads = [];
      querySnapshot.forEach((doc) => {
        ads.push({
          id: doc.id,
          ...doc.data()
        });
      });
      return ads;
    } catch (error) {
      throw this.handleFirestoreError(error);
    }
  }

  /**
   * Fetch ads with retry logic
   * @returns {Promise<Array>} Array of ads documents
   */
  async fetchAdsWithRetry() {
    let lastError;
    
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        return await this.fetchAds();
      } catch (error) {
        lastError = error;
        if (attempt < this.retryAttempts) {
          await this.delay(this.retryDelay);
          this.retryDelay *= 2; // Exponential backoff
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Transform Firebase ads data to match current component format
   * @param {Array} firebaseAds - Raw Firebase ads data
   * @returns {Array} Transformed ads data compatible with existing component
   */
  transformAdsForComponent(firebaseAds) {
    if (!Array.isArray(firebaseAds)) {
      return [];
    }

    const filteredAds = firebaseAds.filter(ad => {
      if (!ad || !ad.isActive) {
        return false;
      }

      // Type-specific validation for all ad types
      const type = ad.type || 'image';
      let hasValidContent = false;
      
      switch (type) {
        case 'image': {
          const imageUrl = ad.content?.imageUrl || ad.imageUrl;
          hasValidContent = !!imageUrl;
          break;
        }
        case 'video': {
          const videoUrl = ad.content?.videoUrl;
          hasValidContent = !!videoUrl;
          break;
        }
        case 'poll': {
          const question = ad.content?.question;
          const options = ad.content?.options;
          hasValidContent = !!(question && options?.length > 0);
          break;
        }
        case 'testimonial': {
          const patientName = ad.content?.patientName;
          const reviewText = ad.content?.reviewText;
          hasValidContent = !!(patientName && reviewText);
          break;
        }
        case 'information': {
          const infoTitle = ad.content?.title;
          const infoMessage = ad.content?.message;
          hasValidContent = !!(infoTitle && infoMessage);
          break;
        }
        default: {
          hasValidContent = false;
          break;
        }
      }
      
      return hasValidContent;
    });

    const transformedAds = filteredAds.map(ad => {
      const type = ad.type || 'image';
      let baseAd, firebaseMetadata;
      
      // Transform based on ad type
      switch (type) {
        case 'image': {
          const imageUrl = ad.content?.imageUrl || ad.imageUrl;
          const linkUrl = ad.content?.linkUrl || ad.linkUrl || '';
          
          baseAd = {
            configName: "ads",
            desc: imageUrl,
            label: linkUrl,
            val: ad.id
          };
          
          firebaseMetadata = {
            imageUrl: imageUrl,
            linkUrl: linkUrl,
            altText: ad.content?.altText || ''
          };
          break;
        }
          
        case 'video': {
          baseAd = {
            configName: "ads", 
            desc: ad.content.thumbnailUrl || ad.content.videoUrl, // Use thumbnail as preview
            label: ad.content.linkUrl || '',
            val: ad.id
          };
          
          firebaseMetadata = {
            videoUrl: ad.content.videoUrl,
            thumbnailUrl: ad.content.thumbnailUrl,
            linkUrl: ad.content.linkUrl || '',
            autoplay: ad.content.autoplay || false,
            muted: ad.content.muted || true,
            altText: ad.content.altText || ''
          };
          break;
        }
          
        case 'poll': {
          baseAd = {
            configName: "ads",
            desc: ad.content.backgroundUrl || '', // Background image if available
            label: ad.content.linkUrl || '',
            val: ad.id
          };
          
          firebaseMetadata = {
            question: ad.content.question,
            options: ad.content.options || [],
            linkUrl: ad.content.linkUrl || '',
            backgroundUrl: ad.content.backgroundUrl || '',
            showResults: ad.content.showResults || false,
            altText: ad.content.altText || ''
          };
          break;
        }
          
        case 'testimonial': {
          baseAd = {
            configName: "ads",
            desc: ad.content.patientPhoto || '', // Patient photo as preview
            label: ad.content.linkUrl || '',
            val: ad.id
          };
          
          firebaseMetadata = {
            patientName: ad.content.patientName,
            patientPhoto: ad.content.patientPhoto || '',
            reviewText: ad.content.reviewText,
            rating: ad.content.rating || 5,
            linkUrl: ad.content.linkUrl || '',
            altText: ad.content.altText || ''
          };
          break;
        }
          
        case 'information': {
          baseAd = {
            configName: "ads",
            desc: '', // No preview image for information cards
            label: ad.content.linkUrl || '',
            val: ad.id
          };
          
          firebaseMetadata = {
            title: ad.content.title,
            message: ad.content.message,
            category: ad.content.category || 'default',
            icon: ad.content.icon || ad.content.category,
            date: ad.content.date || ad.updatedAt,
            linkUrl: ad.content.linkUrl || '',
            altText: ad.content.altText || ''
          };
          break;
        }
          
        default: {
          baseAd = {
            configName: "ads",
            desc: '',
            label: '',
            val: ad.id
          };
          firebaseMetadata = {};
        }
      }
      
      // Transformation complete

      return {
        ...baseAd,
        _firebase: {
          type: type,
          title: ad.title || '',
          priority: ad.priority || 0,
          // Which surfaces this ad is eligible for. Sources without placement
          // flags leave both true, matching the pre-placement behaviour.
          showInCarousel: ad.showInCarousel !== false,
          showInPopup: ad.showInPopup !== false,
          display: ad.display || {},
          updatedAt: ad.updatedAt,
          ...firebaseMetadata
        }
      };
    });

    const sortedAds = transformedAds.sort((a, b) => (a._firebase.priority || 0) - (b._firebase.priority || 0));
    
    return sortedAds;
  }

  /**
   * Get formatted ads data ready for Vuex store
   * @returns {Promise<Array>} Formatted ads data
   */
  async getFormattedAds() {
    try {
      const rawAds = await this.fetchAdsWithRetry();
      return this.transformAdsForComponent(rawAds);
    } catch (error) {
      return []; // Return empty array on failure
    }
  }

  /**
   * Handle Firestore-specific errors
   * @param {Error} error - Original error
   * @returns {Error} Enhanced error with user-friendly message
   */
  handleFirestoreError(error) {
    if (error.code) {
      switch (error.code) {
        case 'permission-denied':
          return new Error('Access denied. Please check Firestore security rules.');
        case 'unavailable':
          return new Error('Firestore service temporarily unavailable. Please try again.');
        case 'not-found':
          return new Error('Ads collection not found. Please check Firestore setup.');
        case 'failed-precondition':
          return new Error('Firestore index required. Check Firebase console.');
        default:
          return new Error(`Firestore error: ${error.message}`);
      }
    }
    return error;
  }

  /**
   * Utility function for delay
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if Firebase is available
   * @returns {boolean} True if Firebase is initialized
   */
  isFirebaseAvailable() {
    return !!(db && window.Firebase);
  }

  /**
   * Get connection status
   * @returns {Object} Connection status information
   */
  getConnectionStatus() {
    return {
      firebaseInitialized: !!db,
      firebaseCDNLoaded: !!window.Firebase,
      ready: this.isFirebaseAvailable()
    };
  }
}

// Export singleton instance
const firebaseAdsService = new FirebaseAdsService();
export default firebaseAdsService;